import { useEffect, useRef, useState } from "react";
import DevotionReferences from "../DevotionReferences";
import { api } from "@/lib/api";
import { BookOpen, Play, Pause, Square, ArrowRight } from "lucide-react";

const APP_ORIGIN = (process.env.REACT_APP_APP_URL || "https://app.bbcbarani.org").replace(/\/$/, "");

function bibleTarget(verse) {
  const match = /^(.*?)\s+(\d+)(?::(\d+)(?:\s*[-–]\s*(\d+))?)?$/.exec((verse?.reference || "").trim());
  const book = verse?.book || match?.[1]?.trim();
  const chapter = Number(verse?.chapter || match?.[2]);
  const start = Number(verse?.verse_start || match?.[3]);
  const end = Number(verse?.verse_end || match?.[4] || match?.[3]);
  if (!book || !Number.isInteger(chapter) || chapter < 1) return null;
  const params = new URLSearchParams({ book, chapter: String(chapter), highlight: start > 0 ? "true" : "false", source: "daily-verse" });
  if (start > 0) { params.set("verse", String(start)); params.set("verseStart", String(start)); }
  if (end > 0) params.set("verseEnd", String(end));
  return `${APP_ORIGIN}/bible/chapter?${params.toString()}`;
}

function devotionTarget(devotion) {
  if (devotion?.date) return `${APP_ORIGIN}/devotion?date=${encodeURIComponent(devotion.date)}`;
  return devotion?.id ? `${APP_ORIGIN}/devotion/${encodeURIComponent(devotion.id)}` : `${APP_ORIGIN}/devotion`;
}

function dateLabel(date) {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const [year, month, day] = date.split("-").map(Number);
  return new Intl.DateTimeFormat(undefined, { dateStyle: "long", timeZone: "UTC" }).format(new Date(Date.UTC(year, month - 1, day)));
}

function devotionExcerpt(message, max = 420) {
  const text = (message || "").trim().replace(/\r\n/g, "\n");
  const firstParagraph = text.split(/\n\s*\n/).find(part => part.trim())?.trim() || text;
  if (firstParagraph.length <= max) return firstParagraph;
  const clipped = firstParagraph.slice(0, max);
  const end = clipped.lastIndexOf(" ");
  return `${clipped.slice(0, end > max * 0.65 ? end : max).trimEnd()}…`;
}

function LoadingCard({ label, compact = false }) {
  return <div aria-label={`Loading ${label}`} aria-busy="true" className={`animate-pulse rounded-2xl border border-[#E5E0D8] bg-white ${compact ? "p-6" : "p-7"}`}>
    <div className="h-3 w-36 rounded bg-[#E8E4DE]" /><div className={`mt-5 rounded bg-[#F0EDE8] ${compact ? "h-10 w-4/5" : "h-6 w-full"}`} />{!compact && <><div className="mt-3 h-4 w-full rounded bg-[#F0EDE8]" /><div className="mt-2 h-4 w-5/6 rounded bg-[#F0EDE8]" /></>}
  </div>;
}

export default function VerseOfDay() {
  const [verse, setVerse] = useState(null);
  const [devotion, setDevotion] = useState(null);
  const [verseLoading, setVerseLoading] = useState(true);
  const [devotionLoading, setDevotionLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get("/daily-verses/today").then(r => { if (active) setVerse(r.data); }).catch(() => {}).finally(() => { if (active) setVerseLoading(false); });
    api.get("/devotions/today").then(r => { if (active) setDevotion(r.data); }).catch(() => {}).finally(() => { if (active) setDevotionLoading(false); });
    return () => {
      active = false;
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const handlePlayPause = async () => {
    if (!devotion?.audio_url) return;
    if (!audioRef.current) {
      const audio = new Audio(devotion.audio_url);
      audio.preload = "none";
      audio.onended = () => setIsPlaying(false);
      audio.onpause = () => setIsPlaying(false);
      audioRef.current = audio;
    }
    if (audioRef.current.paused) {
      try { await audioRef.current.play(); setIsPlaying(true); }
      catch { setIsPlaying(false); }
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setIsPlaying(false);
  };

  if (!verseLoading && !devotionLoading && !verse && !devotion) return null;

  const verseHref = bibleTarget(verse);
  return <section id="daily" data-testid="verse-of-day" className="py-12 sm:py-14 bg-gradient-to-b from-[#F3EFEA] to-[#FAF9F6]">
    <div className="max-w-3xl mx-auto px-5 sm:px-6 space-y-5 sm:space-y-6">
      {verseLoading ? <LoadingCard label="Daily Bible Verse" compact /> : verse ? <article data-testid="verse-of-day-card" className="p-6 sm:p-7 bg-white border border-[#E5E0D8] rounded-2xl shadow-[0_12px_36px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C6661]"><BookOpen className="w-4 h-4 text-[#D4A373]" aria-hidden="true" /><h2>Daily Bible Verse</h2></div>
        {verseHref ? <a href={verseHref} data-testid="daily-verse-bible-link" aria-label={`Open ${verse.reference} in the Bible reader`} className="group mt-4 block rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2C4C3B]">
          <blockquote className="font-serif-display text-2xl sm:text-3xl leading-snug text-[#1C201E] group-hover:text-[#2C4C3B]">“{verse.text}”</blockquote>
          <p className="mt-3 text-sm text-[#2C4C3B] font-semibold">{verse.reference} <span className="font-normal text-[#5C6661]">· KJV</span><ArrowRight className="inline w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" aria-hidden="true" /></p>
        </a> : <><blockquote className="mt-4 font-serif-display text-2xl sm:text-3xl leading-snug text-[#1C201E]">“{verse.text}”</blockquote><p className="mt-3 text-sm text-[#2C4C3B] font-semibold">{verse.reference} · KJV</p></>}
        {verse.date && <p className="mt-2 text-xs text-[#6A746E]">{dateLabel(verse.date)}</p>}
      </article> : null}

      {devotionLoading ? <LoadingCard label="Daily Devotion" /> : devotion ? <article data-testid="devotion-card" className="p-6 sm:p-8 bg-[#2C4C3B] text-white rounded-2xl shadow-[0_14px_40px_rgba(31,55,42,0.14)]">
        <div className="flex flex-wrap justify-between items-start gap-3"><div><p className="text-xs uppercase tracking-[0.2em] text-[#E5B886]">Daily Devotion</p>{devotion.date && <p className="mt-1 text-sm text-white/75">{dateLabel(devotion.date)}</p>}</div>
          {devotion.audio_url && <div className="flex items-center gap-2" aria-label="Devotion narration controls"><button type="button" onClick={handlePlayPause} aria-label={isPlaying ? "Pause devotion narration" : "Play devotion narration"} className="p-2.5 rounded-full bg-[#D4A373] text-[#24392D] hover:bg-[#e0b687] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">{isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}</button>{isPlaying && <button type="button" onClick={handleStop} aria-label="Stop devotion narration" className="p-2.5 rounded-full bg-white/15 text-white hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"><Square className="w-4 h-4 fill-current" /></button>}</div>}
        </div>
        <h2 className="mt-3 font-serif-display text-3xl leading-tight">{devotion.title}</h2>
        {devotion.reference && <DevotionReferences devotion={devotion} className="mt-3 inline-block text-sm font-medium text-[#E5B886] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-white" />}
        {devotion.reference_error && <p role="alert" className="mt-2 text-sm">{devotion.reference_error}</p>}
        {devotion.reference_text && <p className="mt-3 italic text-sm text-white/85 leading-relaxed">“{devotion.reference_text}”</p>}
        <p className="mt-4 text-sm sm:text-base leading-relaxed text-white/90 whitespace-pre-line">{devotionExcerpt(devotion.message)}</p>
        <a data-testid="read-full-devotion" href={devotionTarget(devotion)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-[#2C4C3B] px-5 py-2.5 text-sm font-semibold hover:bg-[#F3EFEA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Read Full Devotion <ArrowRight className="w-4 h-4" aria-hidden="true" /></a>
      </article> : null}
    </div>
  </section>;
}
